// OLDPASSION Memory Game — behaviour. Static copy + art come from the Liquid section;
// this file never hardcodes customer-facing German prose (ARIA announcement strings
// come from data-* on the root, with neutral fallbacks). Pure pieces
// (buildDeck/createGame/createScreens/formatTime) are exported and tested in Node;
// mountMemory is the browser glue.

// --- Pure: deck ---------------------------------------------------------------
// cards: [{src, alt, url}] = the full pool (may be larger than `pairs`). Randomly
// samples `pairs` distinct faces from the pool so the board differs every game and
// niemand kann sich Positionen über Runden merken (pads with placeholders if the pool
// is short), duplicates each into a pair, then Fisher-Yates shuffles positions with an
// injectable rng. Returns cards with a stable `id` = array position and a `pairKey`
// shared by the two halves of a pair.
export function buildDeck(cards, pairs, rng = Math.random) {
  // Shuffle a copy of the pool, then take the first `pairs` as this game's faces.
  const pool = Array.isArray(cards) ? cards.slice() : [];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = pool[i];
    pool[i] = pool[j];
    pool[j] = tmp;
  }
  const faces = [];
  for (let i = 0; i < pairs; i++) {
    faces.push(pool[i] || { src: "", alt: `Karte ${i + 1}`, url: "" });
  }
  const deck = [];
  faces.forEach((face, pairKey) => {
    deck.push({ pairKey, src: face.src || "", alt: face.alt || "", url: face.url || "" });
    deck.push({ pairKey, src: face.src || "", alt: face.alt || "", url: face.url || "" });
  });
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = deck[i];
    deck[i] = deck[j];
    deck[j] = tmp;
  }
  return deck.map((c, id) => ({ id, ...c }));
}

// --- Pure: game logic ---------------------------------------------------------
export function createGame(deck) {
  const total = deck.length;
  const matched = new Set();
  let flipped = [];
  let moves = 0;
  let locked = false; // true while a mismatched pair waits to be flipped back

  return {
    get moves() { return moves; },
    get matchedPairs() { return matched.size / 2; },
    isComplete() { return total > 0 && matched.size === total; },
    isFaceUp(i) { return matched.has(i) || flipped.includes(i); },
    flip(index) {
      if (locked) return { accepted: false };
      if (index < 0 || index >= total) return { accepted: false };
      if (matched.has(index) || flipped.includes(index)) return { accepted: false };
      flipped.push(index);
      if (flipped.length === 1) return { accepted: true, faceUp: [index] };
      moves += 1;
      const [a, b] = flipped;
      if (deck[a].pairKey === deck[b].pairKey) {
        matched.add(a);
        matched.add(b);
        flipped = [];
        return { accepted: true, matched: [a, b], moves, complete: this.isComplete() };
      }
      locked = true;
      return { accepted: true, mismatched: [a, b], moves };
    },
    clearMismatch() {
      flipped = [];
      locked = false;
    },
  };
}

// --- Pure: screen state machine ----------------------------------------------
export function createScreens() {
  return {
    state: "intro", // v1: intro | game | win | done | leaderboard | blocked | error; v2 adds loading | start
    toGame() { if (this.state !== "intro") throw new Error("cannot toGame from " + this.state); this.state = "game"; },
    toWin() { if (this.state !== "game") throw new Error("cannot toWin from " + this.state); this.state = "win"; },
    toDone() { if (this.state !== "win") throw new Error("cannot toDone from " + this.state); this.state = "done"; },
    set(s) { this.state = s; }, // non-linear: game(replay) | win(restore) | leaderboard | blocked | error | intro
  };
}

// --- Pure: v2 game against the room ------------------------------------------
// The server holds the board. Every reveal carries a sequence number; a lost answer is asked
// for again with the same number and comes back identical (also a pair, also the final one
// with the finish token). Two wrong open cards block nothing: the next tap may be one of them,
// the server closes both first. One request at a time: the room keeps the answers of the last
// two numbers only, so a resend is always of the one number that is out.
export function createRoomGame({ pairs, reveal }) {
  const total = pairs * 2;
  const matched = new Set();
  let open = [];        // what the server reported open after the last answer
  let moves = 0;
  let pairsFound = 0;
  let seq = 0;
  let pending = null;   // { pos, seq } while an answer is outstanding
  let busy = false;
  let finish = null;

  function apply(body) {
    (body.matched || []).forEach((i) => matched.add(i));
    open = (body.open || []).slice();
    moves = body.moves;
    pairsFound = body.pairsFound;
    if (body.finished && body.finishToken) finish = { finishToken: body.finishToken, timeMs: body.timeMs, eligible: body.eligible, reason: body.reason };
    const out = { accepted: true, pos: pending.pos, img: body.img, pair: body.pair, matched: body.matched || [], closed: body.closed || [], open: open.slice(), finished: !!body.finished, repeated: !!body.repeated, finish };
    pending = null;
    return out;
  }

  async function send() {
    let r = null;
    for (let attempt = 0; attempt < 2 && !r; attempt++) {
      try {
        const res = await reveal(pending.pos, pending.seq);
        // The room was busy or failed (any 5xx) or the answer arrived cut off (200 without a readable body): once more with
        // the same number; the room answers a repeated number like the first time.
        if (res.status >= 500 || (res.status === 200 && !res.body)) continue;
        r = res;
      } catch { /* network: once more with the same number */ }
    }
    if (!r) return { accepted: false, error: "network" }; // pending stays, retry() sends it again
    if (r.status === 200) return apply(r.body);
    pending = null;
    if (r.status === 404 || r.status === 400) return { accepted: false, error: "lost" };
    return { accepted: false, error: (r.body && r.body.error) || ("http " + r.status) };
  }

  return {
    get moves() { return moves; },
    get matchedPairs() { return pairsFound; },
    get finish() { return finish; },
    get pending() { return pending; },
    isComplete() { return finish !== null; },
    isFaceUp(i) { return matched.has(i) || open.includes(i); },
    async tap(pos) {
      if (busy || pending || finish) return { accepted: false };
      if (!Number.isInteger(pos) || pos < 0 || pos >= total) return { accepted: false };
      if (matched.has(pos) || (open.length === 1 && open[0] === pos)) return { accepted: false };
      pending = { pos, seq: ++seq };
      busy = true;
      try { return await send(); } finally { busy = false; }
    },
    async retry() {
      if (busy || !pending) return { accepted: false };
      busy = true;
      try { return await send(); } finally { busy = false; }
    },
  };
}

// Which version runs: the section setting can force one (an emergency switch in the theme editor),
// otherwise the worker says (GET /v2/config); no usable answer means v1 (an older worker).
export function resolveMode(setting, cfg) {
  if (setting === "v1" || setting === "v2") return setting;
  return cfg && cfg.mode === "v2" ? "v2" : "v1";
}

// The browser names its own start, so a lost start answer can be asked for again (same id, fresh
// human check). 32 lowercase hex characters from crypto.getRandomValues; the id is a bearer secret
// for that game: never in a URL, a log or analytics.
export function newStartId(fill = (arr) => globalThis.crypto.getRandomValues(arr)) {
  const bytes = fill(new Uint8Array(16));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

// --- Helpers ------------------------------------------------------------------
export function formatTime(ms) {
  const safe = Math.max(0, Math.floor(ms));
  const totalS = Math.floor(safe / 1000);
  const mm = String(Math.floor(totalS / 60)).padStart(2, "0");
  const ss = String(totalS % 60).padStart(2, "0");
  const tenths = Math.floor((safe % 1000) / 100);
  return `${mm}:${ss}.${tenths}`;
}

// Ein Rabattcode pro Runde: der Worker schickt beim zweiten oder dritten Versuch
// derselben Runde `codeIssued: false` und keinen Code. Ältere Worker-Antworten kennen
// das Feld nicht, tragen aber einen Code, der dann weiter gilt.
export function hasIssuedCode(data) {
  return !(data.codeIssued === false || !data.code);
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

// Copy helper: Clipboard API in secure contexts (HTTPS / localhost), execCommand
// fallback for insecure ones (e.g. a LAN-IP preview over plain http, where
// navigator.clipboard is absent). Returns true on success.
async function copyText(text) {
  try {
    if (navigator.clipboard && globalThis.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to legacy */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length); // iOS Safari
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

function safeStore(provided) {
  if (provided) return provided;
  try {
    const s = globalThis.sessionStorage;
    s.setItem("__mg_probe", "1"); // exercise writability, not just presence
    s.removeItem("__mg_probe");
    return s;
  } catch {
    const m = {};
    return { getItem: (k) => (k in m ? m[k] : null), setItem: (k, v) => { m[k] = v; }, removeItem: (k) => { delete m[k]; } };
  }
}

// --- Browser glue -------------------------------------------------------------
// The public ranking: the top 10, the player's own row if it is further down, and a button for the rest
// (Roman 16.10.2026: «mehr anzeigen», no endless list).
const LB_TOP = 10;
export function leaderboardHtml(entries, mineRank, showAll) {
  const shown = showAll || entries.length <= LB_TOP ? entries : entries.filter((e) => e.rank <= LB_TOP || (mineRank && e.rank === mineRank));
  const rows = shown
    .map((e) => `<li class="mg-lb__row${mineRank && e.rank === mineRank ? " is-you" : ""}"><span class="mg-lb__rank">${e.rank}</span><span class="mg-lb__name">${escapeHtml(e.name)}</span><span class="mg-lb__time">${formatTime(e.timeMs)}</span></li>`)
    .join("");
  return shown.length < entries.length ? rows + `<li class="mg-lb__more"><button type="button" class="mg-lb__more-btn" data-mg-lb-more>Alle ${entries.length} Zeiten anzeigen</button></li>` : rows;
}

export function mountMemory(root, opts = {}) {
  if (root.dataset.mgMounted) return;
  root.dataset.mgMounted = "1";

  const apiBase = opts.apiBase ?? root.dataset.api ?? "";
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  const rng = opts.rng ?? Math.random;
  const mismatchMs = opts.mismatchMs ?? 900;
  let pairs = opts.pairs ?? Number(root.dataset.pairs || "8");
  const backUrl = opts.back ?? root.dataset.back ?? "";
  const shopUrl = opts.shop ?? root.dataset.shop ?? "";
  const store = safeStore(opts.store);
  const PERSIST_KEY = "mg_finish";

  // ARIA announcement strings: from data-* (set by the section), with neutral fallbacks.
  const SR = {
    match: root.dataset.srMatch || "Paar gefunden",
    mismatch: root.dataset.srMismatch || "Kein Paar",
    win: root.dataset.srWin || "Geschafft",
    done: root.dataset.srDone || "Code bereit",
    blocked: root.dataset.srBlocked || "Schon dabei",
    lastPair: root.dataset.srLastpair || "Nur noch ein Paar",
  };

  let cards = opts.cards;
  if (!cards) {
    const tag = root.querySelector("[data-mg-cards]");
    try { cards = tag ? JSON.parse(tag.textContent) : []; } catch { cards = []; }
  }

  const screens = createScreens();
  const board = root.querySelector("[data-mg-slot='board']");
  let deck = [];
  let game = null;
  let startToken = null;
  let finishToken = null;
  let timeMs = 0;
  let timerId = null;
  let timerStart = 0;
  let claiming = false;
  let userActed = false;
  let youRank = null;
  let retryFn = null;

  const $ = (sel) => root.querySelector(sel);
  const $$ = (sel) => Array.from(root.querySelectorAll(sel));
  const fill = (slot, text) => { $$(`[data-mg-slot='${slot}']`).forEach((el) => { el.textContent = text; }); };
  const announce = (msg) => { const el = $("[data-mg-slot='sr']"); if (el) el.textContent = msg; };

  const render = () => { root.dataset.mgState = screens.state; };
  const focusScreen = () => {
    if (!userActed) return; // never yank focus on initial mount / restore
    const screen = root.querySelector(`.mg__screen--${screens.state}`);
    if (!screen) return;
    const target = screen.querySelector("[data-mg-autofocus], h2, .mg__btn, .mg__input");
    if (target) {
      if (target.tagName === "H2" && !target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
      try { target.focus(); } catch { /* noop */ }
    }
  };
  const go = (state) => { screens.set(state); render(); focusScreen(); };
  // Leave the game: straight to the shop if a URL is set, otherwise back to the intro.
  const leaveToShop = () => {
    if (shopUrl) { try { window.location.href = shopUrl; return; } catch { /* noop */ } }
    go("intro");
  };

  const cardEl = (id) => board && board.querySelector(`[data-mg-card='${id}']`);
  const setUp = (id, on) => {
    const el = cardEl(id);
    if (!el) return;
    // will-change only while the flip is actually running (cleared on transitionend,
    // with a timeout fallback because background tabs never fire transitionend).
    el.classList.add("is-flipping");
    const inner = el.querySelector(".mg-card__inner");
    const done = () => { el.classList.remove("is-flipping"); if (inner) inner.removeEventListener("transitionend", done); };
    if (inner) inner.addEventListener("transitionend", done);
    setTimeout(done, 650);
    el.classList.toggle("is-up", on);
    el.setAttribute("aria-pressed", on ? "true" : "false");
    const alt = (deck[id] && deck[id].alt) || "";
    el.setAttribute("aria-label", on && alt ? alt : (root.dataset.cardLabel || "Verdeckte Karte"));
  };
  // Decode the face image before flipping so a cold JPEG decode never janks the rotation.
  const flipUp = async (id) => {
    const el = cardEl(id);
    if (!el) return;
    const img = el.querySelector(".mg-card__img");
    if (img && !img.dataset.decoded && img.decode) {
      try { await img.decode(); } catch { /* noop */ }
      img.dataset.decoded = "1";
    }
    setUp(id, true);
  };
  // Celebratory brass/cream sparks (no library, motion-safe, self-removing).
  const celebrate = () => {
    try { if (matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches) return; } catch { /* noop */ }
    if (typeof document === "undefined") return;
    const layer = document.createElement("div");
    layer.className = "mg__sparks";
    for (let i = 0; i < 34; i++) {
      const s = document.createElement("span");
      s.className = "mg-spark";
      s.style.left = Math.round(rng() * 100) + "%";
      s.style.setProperty("--mg-dx", Math.round((rng() - 0.5) * 120) + "px");
      s.style.setProperty("--mg-rot", Math.round(180 + rng() * 360) + "deg");
      s.style.setProperty("--mg-dur", (1 + rng() * 0.8).toFixed(2) + "s");
      s.style.setProperty("--mg-delay", (rng() * 0.25).toFixed(2) + "s");
      if (i % 3 === 0) s.style.background = "var(--mg-cream)";
      layer.appendChild(s);
    }
    root.appendChild(layer);
    setTimeout(() => layer.remove(), 2400);
  };
  const markWrong = (id, on) => { const el = cardEl(id); if (el) el.classList.toggle("is-wrong", on); };
  const markMatched = (id) => {
    const el = cardEl(id);
    if (!el) return;
    el.classList.add("is-matched", "is-just-matched");
    el.disabled = true;
    setTimeout(() => el.classList.remove("is-just-matched"), 650);
  };
  const updateHud = () => { const mv = $("[data-mg-slot='moves']"); if (mv && game) mv.textContent = String(game.moves); };
  const updatePairs = () => {
    if (!game) return;
    const p = $("[data-mg-slot='pairs']");
    if (p) {
      p.textContent = `${game.matchedPairs}/${pairs}`;
      p.classList.add("is-bump");
      setTimeout(() => p.classList.remove("is-bump"), 260);
    }
    root.style.setProperty("--mg-prog", String(pairs ? game.matchedPairs / pairs : 0));
  };

  const startTimer = () => {
    if (timerId) clearInterval(timerId);
    timerStart = Date.now();
    const tick = () => fill("timer", formatTime(Date.now() - timerStart));
    tick();
    timerId = setInterval(tick, 100);
  };
  const stopTimer = () => { if (timerId) clearInterval(timerId); timerId = null; };

  const persistFinish = () => { try { store.setItem(PERSIST_KEY, JSON.stringify({ finishToken, timeMs })); } catch { /* noop */ } };
  const clearPersist = () => { try { store.removeItem(PERSIST_KEY); } catch { /* noop */ } };

  const showFormError = (msg) => { const el = $("[data-mg-slot='formerror']"); if (el) { el.textContent = msg; el.hidden = false; } };
  const clearFormError = () => { const el = $("[data-mg-slot='formerror']"); if (el) { el.textContent = ""; el.hidden = true; } };

  const errorSlot = $("[data-mg-slot='errortext']");
  const errorDefault = errorSlot ? errorSlot.textContent : "";
  const fail = (retry, err, text) => { console.error("[memory]", err); retryFn = retry; if (errorSlot) errorSlot.textContent = text || errorDefault; go("error"); };

  function renderBoard(d) {
    if (!board) return;
    board.innerHTML = "";
    const cols = Math.max(2, Math.round(Math.sqrt(d.length)));
    board.style.setProperty("--mg-cols", String(cols));
    // Die Reihenzahl braucht das CSS, um die Kartengrösse aus der Bildschirmhöhe zu rechnen.
    board.style.setProperty("--mg-rows", String(Math.ceil(d.length / cols)));
    d.forEach((card, i) => {
      const btn = document.createElement("button");
      btn.className = "mg-card";
      btn.type = "button";
      btn.dataset.mgCard = String(card.id);
      btn.style.setProperty("--mg-i", String(i));
      btn.setAttribute("aria-pressed", "false");
      btn.setAttribute("aria-label", root.dataset.cardLabel || "Verdeckte Karte");
      const face = card.src
        ? `<img class="mg-card__img" src="${escapeHtml(card.src)}" alt="" decoding="async">`
        : `<span class="mg-card__ph">${card.pairKey + 1}</span>`;
      const back = backUrl
        ? `<span class="mg-card__back" style="background-image:url('${escapeHtml(backUrl)}')"></span>`
        : `<span class="mg-card__back mg-card__back--logo">OP</span>`;
      btn.innerHTML = `<span class="mg-card__inner">${back}<span class="mg-card__face">${face}</span></span>`;
      board.appendChild(btn);
    });
    // Pre-decode the faces during idle time so the first flip of each card is already warm.
    if (typeof requestIdleCallback === "function") {
      requestIdleCallback(() => {
        board.querySelectorAll(".mg-card__img").forEach((im) => {
          if (im.decode) im.decode().then(() => { im.dataset.decoded = "1"; }).catch(() => {});
        });
      });
    }
  }

  function renderLeaderboard(slot, entries, mineRank) {
    const el = $(`[data-mg-slot='${slot}']`);
    if (!el) return;
    if (!entries || !entries.length) {
      el.innerHTML = `<li class="mg-lb__empty">${escapeHtml(el.dataset.empty || "")}</li>`;
      return;
    }
    el.innerHTML = leaderboardHtml(entries, mineRank, false);
    const more = el.querySelector("[data-mg-lb-more]");
    if (more) more.addEventListener("click", () => { el.innerHTML = leaderboardHtml(entries, mineRank, true); });
  }

  async function loadLeaderboard(slot, month, mineRank) {
    try {
      const q = month ? `?month=${encodeURIComponent(month)}` : "";
      const res = await fetchImpl(apiBase + "/leaderboard" + q);
      if (!res.ok) throw new Error("leaderboard " + res.status);
      const data = await res.json();
      renderLeaderboard(slot, data.entries, mineRank);
    } catch (err) { console.error("[memory]", err); renderLeaderboard(slot, [], mineRank); }
  }

  function resetGameUi() {
    if (board) board.innerHTML = "";
    fill("timer", formatTime(0));
    const mv = $("[data-mg-slot='moves']"); if (mv) mv.textContent = "0";
    const p = $("[data-mg-slot='pairs']"); if (p) p.textContent = `0/${pairs}`;
  }

  async function beginGame() {
    clearPersist();
    finishToken = null;
    clearFormError();
    const startBtns = $$("[data-mg-act='start'], [data-mg-act='replay']");
    startBtns.forEach((b) => { b.disabled = true; });
    try {
      const res = await fetchImpl(apiBase + "/start", { method: "POST" });
      if (!res.ok) throw new Error("start " + res.status);
      startToken = (await res.json()).startToken;
    } catch (err) {
      startBtns.forEach((b) => { b.disabled = false; });
      fail(beginGame, err);
      return;
    }
    deck = buildDeck(cards, pairs, rng);
    game = createGame(deck);
    renderBoard(deck);
    const hint = $("[data-mg-slot='hint']");
    if (hint) hint.hidden = !!store.getItem("mg_onboard");
    updateHud();
    updatePairs();
    fill("timer", formatTime(0));
    go("game");
    const firstCard = board && board.querySelector("[data-mg-card]");
    if (firstCard && userActed) { try { firstCard.focus({ preventScroll: true }); } catch { /* noop */ } }
    startTimer();
    startBtns.forEach((b) => { b.disabled = false; });
  }

  async function finishGame() {
    stopTimer();
    try {
      const res = await fetchImpl(apiBase + "/finish", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ startToken, moves: game.moves }),
      });
      if (!res.ok) throw new Error("finish " + res.status);
      const data = await res.json();
      finishToken = data.finishToken;
      timeMs = data.timeMs;
      persistFinish();
      fill("time", formatTime(timeMs));
      go("win");
      celebrate();
      announce(`${SR.win}. ${formatTime(timeMs)}`);
    } catch (err) { fail(finishGame, err); }
  }

  function onCardClick(id) {
    if (!game) return;
    const r = game.flip(id);
    if (!r.accepted) return;
    userActed = true;
    const hint = $("[data-mg-slot='hint']");
    if (hint && !hint.hidden) { hint.hidden = true; try { store.setItem("mg_onboard", "1"); } catch { /* noop */ } }
    flipUp(id);
    updateHud();
    if (r.matched) {
      r.matched.forEach(markMatched);
      updatePairs();
      if (r.complete) { announce(SR.match); finishGame(); }
      else announce(game.matchedPairs === pairs - 1 ? SR.lastPair : SR.match);
    } else if (r.mismatched) {
      const [a, b] = r.mismatched;
      markWrong(a, true); markWrong(b, true);
      announce(SR.mismatch);
      setTimeout(() => {
        setUp(a, false); setUp(b, false);
        markWrong(a, false); markWrong(b, false);
        game.clearMismatch();
      }, mismatchMs);
    }
  }

  async function onClaim(form) {
    if (claiming || screens.state !== "win") return;
    claiming = true;
    clearFormError();
    const fd = new FormData(form);
    // Newsletter is mandatory: participation = subscription. The checkbox is `required`
    // in markup; this guards a JS-path submit and gives a friendly message.
    if (!fd.get("newsletter")) {
      showFormError(root.dataset.consentError || "Bitte bestätige die Newsletter-Anmeldung, sie gehört zur Teilnahme.");
      claiming = false;
      return;
    }
    const payload = {
      firstName: fd.get("firstName"),
      lastName: fd.get("lastName"),
      email: fd.get("email"),
      newsletter: !!fd.get("newsletter"),
      finishToken,
    };
    const btn = form.querySelector("button[type=submit]");
    if (btn) btn.disabled = true;
    try {
      const send = () => fetchImpl(apiBase + "/claim", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      });
      let res = await send();
      // Shopify left the code unclear (502 unclear): the next request asks Shopify for the code first. Twice more,
      // quietly; a lasting Shopify error answers unclear again and again, so then the message below.
      const unclear = async (r) => r.status === 502 && ((await r.json().catch(() => null)) || {}).reason === "unclear";
      for (let n = 1; n <= 2 && (await unclear(res)); n++) {
        await new Promise((resolve) => setTimeout(resolve, 1000 * n));
        res = await send();
      }
      if (res.status === 429) { clearPersist(); go("blocked"); announce(SR.blocked); return; }
      if (res.status === 503) { showFormError(root.dataset.busyText || "Gerade sehr viel los. Versuch es gleich nochmal."); return; }
      if (res.status === 400) {
        // Most likely a restored finish token that expired server-side: a retry would
        // loop forever, so clear it and point the player at the replay button.
        let reason = "";
        try { reason = ((await res.json()) || {}).error || ""; } catch { /* noop */ }
        if (reason === "invalid token") {
          clearPersist();
          finishToken = null;
          showFormError(root.dataset.expiredText || "Dieses Spiel ist zu lange her, um es noch einzutragen. Spiel einfach nochmal, dann bekommst du deinen Code.");
        } else {
          showFormError(root.dataset.retryText || "Kurz gehakt. Versuch es nochmal, deine Zeit ist gespeichert.");
        }
        return;
      }
      if (!res.ok) throw new Error("claim " + res.status);
      const data = await res.json();
      clearPersist();
      // Ein Code pro Runde: ohne Code bleibt der Slot leer, Kopieren/Einlösen blenden
      // per CSS aus und der Done-Screen erklärt, warum nur die Zeit gezählt hat.
      const withCode = hasIssuedCode(data);
      root.dataset.mgCode = withCode ? "1" : "0";
      fill("code", withCode ? data.code : "");
      const nocodeEl = $("[data-mg-slot='nocode']");
      if (nocodeEl) nocodeEl.hidden = withCode;
      const doneHead = $("[data-mg-slot='done-headline']");
      if (doneHead) {
        const orig = doneHead.dataset.label || (doneHead.dataset.label = doneHead.textContent.trim());
        doneHead.textContent = withCode ? orig : (doneHead.dataset.nocode || orig);
      }
      // Redeem CTA: use the per-user link that auto-applies the code + lands on /collections/all.
      const redeemEl = $("[data-mg-slot='redeem']");
      if (redeemEl && data.redeemUrl) redeemEl.href = data.redeemUrl;
      fill("time", formatTime(data.timeMs));
      // Honest closing (S16, S20): what really happened with the newsletter, and why a time does not count.
      const nlText = { subscribed: root.dataset.newsletterSubscribed, deferred: root.dataset.newsletterDeferred, pending: root.dataset.newsletterDeferred, unchanged: root.dataset.newsletterUnchanged }[data.newsletter] || "";
      fill("newsletter", nlText);
      const unv = $("[data-mg-slot='unverified']"); if (unv) unv.hidden = data.reason !== "unverified";
      const inel = $("[data-mg-slot='ineligible']"); if (inel) inel.hidden = data.reason === "unverified";
      root.dataset.mgEligible = data.eligible ? "1" : "0";
      youRank = data.eligible ? data.rank : null;
      const rankEl = $("[data-mg-slot='rank']");
      if (rankEl) rankEl.textContent = youRank ? String(youRank) : "";
      go("done");
      celebrate();
      announce(SR.done);
      loadLeaderboard("lb-done", data.month, youRank);
    } catch (err) {
      console.error("[memory]", err);
      showFormError(root.dataset.retryText || "Kurz gehakt. Versuch es nochmal, deine Zeit ist gespeichert.");
    } finally {
      claiming = false;
      if (btn) btn.disabled = false;
    }
  }

  // v2 plugs in here: the room engine replaces beginGame/onCardClick/finishGame and reuses the rest.
  const internal = {
    root, board, $, $$, fill, announce, go, screens, cardEl, setUp, markMatched, markWrong, updateHud, updatePairs, startTimer, stopTimer, fail, resetGameUi, celebrate, SR, store, fetchImpl, apiBase,
    get pairs() { return pairs; }, setPairs(n) { pairs = n; }, setGame(g) { game = g; }, markActed() { userActed = true; }, get acted() { return userActed; },
    setFinish(token, ms) { finishToken = token; timeMs = ms; persistFinish(); fill("time", formatTime(ms)); },
    clearFinish() { clearPersist(); finishToken = null; },
  };
  const engine = opts.engine ? opts.engine(internal) : null;

  root.addEventListener("click", (e) => {
    const cardBtn = e.target.closest("[data-mg-card]");
    if (cardBtn && root.contains(cardBtn) && (screens.state === "game" || (engine && screens.state === "start"))) {
      if (engine) engine.onCard(Number(cardBtn.dataset.mgCard)); else onCardClick(Number(cardBtn.dataset.mgCard));
      return;
    }
    const act = e.target.closest("[data-mg-act]");
    if (!act || !root.contains(act)) return;
    const a = act.dataset.mgAct;
    userActed = true;
    if (a === "start" || a === "replay") {
      if (engine) engine.begin(); else beginGame();
    } else if (a === "retry" && retryFn) {
      const fn = retryFn; retryFn = null;
      fn();
    } else if (a === "copy") {
      const code = (($("[data-mg-slot='code']") || {}).textContent || "").trim();
      if (!code) return;
      copyText(code).then((ok) => {
        if (!ok) return;
        const orig = act.dataset.label || (act.dataset.label = act.textContent.trim());
        act.textContent = act.dataset.copied || "Kopiert!";
        act.classList.add("is-copied");
        setTimeout(() => { act.textContent = orig; act.classList.remove("is-copied"); }, 1800);
      });
    } else if (a === "exit") {
      // Mid-game: confirm before discarding progress. Otherwise leave straight away.
      if (screens.state === "game" && game && !game.isComplete()) root.classList.add("is-confirming");
      else leaveToShop();
    } else if (a === "exit-stay") {
      root.classList.remove("is-confirming");
    } else if (a === "exit-restart") {
      root.classList.remove("is-confirming");
      stopTimer();
      if (engine) engine.begin(); else go("intro");
    } else if (a === "exit-go") {
      root.classList.remove("is-confirming");
      stopTimer();
      leaveToShop();
    }
  });

  // Im Vollbild darf sich niemand gefangen fühlen: ESC macht dasselbe wie das X,
  // und bei offener Abbruch-Nachfrage bringt es einen zurück ins Spiel. In "win"/"done"
  // bleibt ESC wirkungslos, sonst wäre der Code mit einem Tastendruck weg.
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    if (root.classList.contains("is-confirming")) { root.classList.remove("is-confirming"); return; }
    if (screens.state !== "game" && !(engine && screens.state === "start")) return;
    userActed = true;
    const exitBtn = $("[data-mg-act='exit']");
    if (exitBtn) exitBtn.click();
  });

  root.addEventListener("submit", (e) => {
    const form = e.target.closest("[data-mg-act='claim']");
    if (!form || !root.contains(form)) return;
    e.preventDefault();
    userActed = true;
    onClaim(form);
  });

  // Restore an unclaimed finished game across a reload (the token may have expired
  // server-side; if so the claim shows an inline error and the player can replay).
  const saved = (() => { try { return JSON.parse(store.getItem(PERSIST_KEY) || "null"); } catch { return null; } })();
  if (saved && saved.finishToken) {
    finishToken = saved.finishToken;
    timeMs = saved.timeMs || 0;
    fill("time", formatTime(timeMs));
    screens.set("win");
  }
  resetGameUi();
  render();
  if (engine) engine.mounted(screens.state);
}

// --- v2 glue -------------------------------------------------------------------
// A time limit on every v2 request: a hanging mobile connection ends as a network failure (and the silent
// second attempt runs) instead of holding the board with the door ajar. Safari before 16 has no AbortSignal.timeout.
function timeLimit(ms) {
  if (typeof AbortSignal.timeout === "function") return AbortSignal.timeout(ms);
  const c = new AbortController();
  setTimeout(() => c.abort(), ms);
  return c.signal;
}

// All eight faces must be loaded and decoded before a rated game starts (Codex 13).
export function preloadImages(urls, timeoutMs = 20000) {
  const one = (u) => new Promise((resolve, reject) => {
    const im = new Image();
    im.decoding = "async";
    im.onload = () => (im.decode ? im.decode().then(() => resolve(im), () => reject(new Error("decode " + u))) : resolve(im));
    im.onerror = () => reject(new Error("image " + u));
    im.src = u;
  });
  return Promise.race([Promise.all(urls.map(one)), new Promise((_, reject) => setTimeout(() => reject(new Error("preload timeout")), timeoutMs))]);
}

// Cloudflare Turnstile, invisible unless a challenge needs a hand. One token per start: take() hands
// it out once, renew() resets the widget for the next start. Without a site key, without the script
// (ad blocker, old browser) or without a token in time the game runs unrated: null.
export function createTurnstile(root, siteKey, { apiWaitMs = 30000, tokenWaitMs = 8000 } = {}) {
  const slot = root.querySelector("[data-mg-slot='turnstile']");
  let widget = null;
  let token = null;
  let waiters = [];
  let failed = !siteKey || !slot;
  let asking = false; // the widget shows a challenge and waits for a hand
  let wait = null;    // the deadline of a waiting take()
  // A waiting take() gets tokenWaitMs, but not while the person reads or clicks the check: the deadline stands still
  // then and starts again in full afterwards. In full, because the token usually follows at once, and a remainder
  // could be nearly nothing for someone who clicked late.
  const arm = () => {
    clearTimeout(wait);
    wait = waiters.length && !asking ? setTimeout(() => { const w = waiters; waiters = []; w.forEach((fn) => fn(null)); }, tokenWaitMs) : null;
  };
  const settle = (t) => { token = t; clearTimeout(wait); const w = waiters; waiters = []; w.forEach((fn) => fn(t)); };
  // A challenge that needs a hand shows the widget: the loading screen stops being busy and says why (S4). Every end
  // of the challenge (done, a token, an error, a timeout, a reset) takes the sentence away again, so it never sticks.
  const needsHand = (on) => {
    if (on === asking) return;
    asking = on;
    arm();
    const screen = root.querySelector(".mg__screen--loading");
    if (screen) { if (on) screen.removeAttribute("aria-busy"); else screen.setAttribute("aria-busy", "true"); }
    const note = root.querySelector("[data-mg-slot='human']");
    if (note) note.hidden = !on;
  };
  const render = () => {
    const ts = globalThis.turnstile;
    if (!ts) return false;
    try {
      widget = ts.render(slot, {
        sitekey: siteKey, action: "jailbird-start", appearance: "interaction-only",
        callback: (t) => { settle(t); needsHand(false); }, "error-callback": () => { settle(null); needsHand(false); },
        "expired-callback": () => { token = null; }, "timeout-callback": () => { settle(null); needsHand(false); },
        "before-interactive-callback": () => needsHand(true), "after-interactive-callback": () => needsHand(false),
        "unsupported-callback": () => { failed = true; settle(null); },
      });
    } catch (e) { console.error("[memory] turnstile", e); failed = true; settle(null); }
    return true;
  };
  if (!failed && !render()) {
    if (typeof document === "undefined") { failed = true; }
    else {
      globalThis.__mgTurnstileReady = () => { render(); };
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=__mgTurnstileReady";
      s.async = true; s.defer = true;
      s.onerror = () => { failed = true; settle(null); };
      document.head.appendChild(s);
      setTimeout(() => { if (widget === null) { failed = true; settle(null); } }, apiWaitMs);
    }
  }
  return {
    take() {
      if (failed) return Promise.resolve(null);
      if (token) { const t = token; token = null; return Promise.resolve(t); }
      return new Promise((resolve) => {
        waiters.push((t) => { if (t) token = null; resolve(t); });
        arm(); // a late token after the deadline stays for the next take()
      });
    },
    renew() {
      settle(null); // whoever still waits gets no token (in practice nobody: renew follows a take that got one)
      needsHand(false);
      if (widget !== null && globalThis.turnstile) { try { globalThis.turnstile.reset(widget); } catch { /* noop */ } }
    },
  };
}

export function createV2Engine(i, opts) {
  const T = i.root.dataset;
  const turnstile = opts.turnstile || createTurnstile(i.root, T.turnstileSitekey || "", { apiWaitMs: opts.apiWaitMs, tokenWaitMs: opts.tokenWaitMs });
  const preload = opts.preload || preloadImages;
  const requestMs = opts.requestMs ?? 10000;
  let startId = null;
  let start = null;   // the answer of /v2/start
  let game = null;
  let miss = null;    // the two wrong cards waiting to flip back
  // Start id and game token travel in the JSON body only: never in a URL, a log or storage.
  const post = (path, body) => i.fetchImpl(i.apiBase + path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body), signal: timeLimit(requestMs) });
  // Only a closed door waits at the edge; a face-up one (one of two wrong doors, or matched) never does.
  const closedDoor = (el) => !!el && !el.classList.contains("is-up") && !el.classList.contains("is-matched");

  function renderBoard(n) {
    if (!i.board) return;
    i.board.innerHTML = "";
    const cols = Math.max(2, Math.round(Math.sqrt(n)));
    i.board.style.setProperty("--mg-cols", String(cols));
    i.board.style.setProperty("--mg-rows", String(Math.ceil(n / cols)));
    const backUrl = T.back || "";
    for (let p = 0; p < n; p++) {
      const btn = document.createElement("button");
      btn.className = "mg-card"; btn.type = "button"; btn.dataset.mgCard = String(p);
      btn.style.setProperty("--mg-i", String(p));
      btn.setAttribute("aria-pressed", "false");
      btn.setAttribute("aria-label", T.cardLabel || "Verdeckte Karte");
      const back = backUrl ? `<span class="mg-card__back" style="background-image:url('${escapeHtml(backUrl)}')"></span>` : `<span class="mg-card__back mg-card__back--logo">OP</span>`;
      btn.innerHTML = `<span class="mg-card__inner">${back}<span class="mg-card__face"><img class="mg-card__img" alt="" decoding="async" data-decoded="1"></span></span>`;
      i.board.appendChild(btn);
    }
  }
  // The face of a card is only known from the server's answer (the board never leaves the room).
  function face(pos, img) {
    const el = i.cardEl(pos);
    if (!el) return;
    const meta = start.images[img];
    const im = el.querySelector(".mg-card__img");
    if (im && meta && im.getAttribute("src") !== meta.url) im.src = meta.url;
    el.setAttribute("aria-label", (meta && meta.alt) || T.cardLabel || "Verdeckte Karte");
  }

  async function begin() {
    i.clearFinish(); i.resetGameUi();
    miss = null; game = null; i.setGame(null);
    i.go("loading");
    startId = newStartId(); // a new game, a new id
    await attemptStart(startId);
  }
  // A failed start is asked for again with the SAME id and a fresh token: if the first request reached
  // the room, the same game comes back (no new check needed); if not, the room is created now. The retry
  // button appears only once the first request has ended (at the latest at its time limit).
  async function attemptStart(id) {
    let token = null;
    try { token = await turnstile.take(); } catch { token = null; }
    if (token) turnstile.renew(); // a used token is gone: the widget gets the next one now, for a retry or the next game (P13)
    if (id !== startId) return;   // a newer start took over
    try { await fetchStart(id, token); } catch (err) { if (id === startId) i.fail(() => { i.go("loading"); return attemptStart(id); }, err); }
  }
  async function fetchStart(id, token) {
    const res = await post("/v2/start", { startId: id, turnstileToken: token });
    if (!res.ok) throw new Error("start " + res.status);
    const answer = await res.json();
    if (id !== startId) return;
    start = answer;
    i.setPairs(start.pairs);
    await ready(id);
  }
  // Images first; on a failure only they are asked for again (the same game, no new check needed).
  async function ready(id) {
    try { await preload(start.images.map((im) => im.url)); }
    catch (err) { if (id === startId) i.fail(() => { i.go("loading"); return ready(id); }, err, T.preloadErrorText); return; }
    if (id !== startId) return;
    const s = start;
    game = createRoomGame({
      pairs: s.pairs,
      reveal: async (pos, seq) => {
        const r = await post("/v2/reveal", { gameToken: s.gameToken, pos, seq });
        let body = null;
        try { body = await r.json(); } catch { body = null; }
        return { status: r.status, body };
      },
    });
    i.setGame(game);
    renderBoard(s.pairs * 2);
    i.updatePairs(); i.fill("timer", formatTime(0));
    // The pair count comes from the worker (PAIRS, its one place) and replaces the number in the start line.
    const hint = i.$("[data-mg-slot='starthint']");
    if (hint) hint.textContent = hint.textContent.replace(/\d+/, String(s.pairs));
    i.go("start");
    const first = i.acted && i.board && i.board.querySelector("[data-mg-card]");
    if (first) { try { first.focus({ preventScroll: true }); } catch { /* noop */ } }
  }
  function lost() {
    i.stopTimer(); game = null; i.setGame(null);
    i.fail(() => begin(), new Error("room lost"), T.lostText);
  }
  // What the room answered. Refused here (a matched door, the one open door): nothing was sent. A lost connection waits
  // for the retry button. A door the room knows better (already open or matched, a refused position, a finished game)
  // keeps the board. Anything else (no game, bad_seq, stale, an unexpected status) ends this game with the lost
  // sentence: never a silently dead board.
  function settle(r) {
    if (!r.accepted) {
      if (!r.error) return;
      const e = String(r.error);
      if (e === "network") i.fail(() => resume(), new Error("reveal: network"));
      else if (!(e.startsWith("already_") || e === "invalid_pos" || e === "finished")) lost();
      return;
    }
    miss = null;
    r.closed.forEach((p) => { i.markWrong(p, false); if (p !== r.pos) i.setUp(p, false); });
    i.setUp(r.pos, true);
    face(r.pos, r.img); // after setUp, which only knows the covered label
    if (r.pair === true) {
      r.matched.forEach((p) => { face(p, r.img); i.markMatched(p); }); // also on a repeated answer (K11)
      i.updatePairs();
      if (!r.finished) i.announce(game.matchedPairs === i.pairs - 1 ? i.SR.lastPair : i.SR.match);
    } else if (r.pair === false) {
      const pair = r.open.slice();
      pair.forEach((p) => i.markWrong(p, true));
      i.announce(i.SR.mismatch);
      miss = pair;
      // Flip back after the pause unless a third door already closed them (the server did it first).
      setTimeout(() => { if (miss !== pair) return; miss = null; pair.forEach((p) => { i.setUp(p, false); i.markWrong(p, false); }); }, start.mismatchPauseMs || 600);
    }
    if (r.finished && r.finish) {
      i.stopTimer();
      i.setFinish(r.finish.finishToken, r.finish.timeMs);
      i.go("win");
      i.celebrate();
      i.announce(`${i.SR.win}. ${formatTime(r.finish.timeMs)}`);
    }
  }
  async function onCard(pos) {
    const g = game;
    if (!g || g.pending) return;
    i.markActed();
    if (i.screens.state === "start") { i.go("game"); i.startTimer(); i.announce(T.srStart || "Die Uhr läuft"); }
    const el = i.cardEl(pos);
    const ajar = closedDoor(el);
    if (ajar) el.classList.add("is-ajar"); // the door waits at the edge until the server answers
    const r = await g.tap(pos);
    if (ajar) el.classList.remove("is-ajar");
    if (g === game) settle(r); // unless a new game began meanwhile
  }
  // After a lost connection: back to the board at once (P14), the same request once more with its number.
  async function resume() {
    const g = game;
    if (!g || !g.pending) return;
    i.go("game");
    const el = i.cardEl(g.pending.pos);
    const ajar = closedDoor(el);
    if (ajar) el.classList.add("is-ajar");
    const r = await g.retry();
    if (ajar) el.classList.remove("is-ajar");
    if (g === game) settle(r);
  }

  return { begin, onCard, mounted(state) { if (state !== "win") return begin(); } };
}

export function mountMemoryV2(root, opts = {}) {
  root.classList.add("mg--v2");
  return mountMemory(root, { ...opts, engine: (internal) => createV2Engine(internal, opts) });
}

// Which version: the section may force one, otherwise the worker says (GET /v2/config, 3 s). A failed, slow
// or non-OK answer counts as no answer, and no answer means v1 (an older worker).
export async function mountAuto(root, opts = {}) {
  const mode = opts.mode ?? root.dataset.mgMode;
  let cfg = null;
  if (mode !== "v1" && mode !== "v2") {
    const fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
    const apiBase = opts.apiBase ?? root.dataset.api ?? "";
    try {
      const res = await fetchImpl(apiBase + "/v2/config", { signal: timeLimit(opts.configTimeoutMs ?? 3000) });
      if (res.ok) cfg = await res.json();
    } catch { /* an older worker or no network: v1 */ }
  }
  if (resolveMode(mode, cfg) === "v2") return mountMemoryV2(root, opts);
  root.classList.remove("mg--v2");
  return mountMemory(root, opts);
}

// Auto-mount on real pages. The dev preview opts out with data-mg-manual.
if (typeof document !== "undefined") {
  const el = document.querySelector(".mg");
  if (el && !el.hasAttribute("data-mg-manual")) mountAuto(el);
}
